import { useEffect, useMemo, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "../lib/api";

function flatten(node, prefix, out) {
  for (const entry of Object.entries(node)) {
    const k = entry[0];
    const v = entry[1];
    const path = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === "object" && !Array.isArray(v)) {
      flatten(v, path, out);
    } else {
      out.push({ path, value: v });
    }
  }
  return out;
}

function deepSet(obj, path, value) {
  const keys = path.split(".");
  const clone = Array.isArray(obj) ? [...obj] : { ...obj };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    cur[k] = Array.isArray(cur[k]) ? [...cur[k]] : { ...cur[k] };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

export default function ConfigEditor({ config, token }) {
  const [draft, setDraft] = useState(config);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(config), [config]);

  const fields = useMemo(() => flatten(draft, "", []), [draft]);
  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(config), [draft, config]);

  const setField = (path, raw) => {
    let value;
    if (raw.trim() === "") value = null;
    else {
      const n = Number(raw);
      value = isNaN(n) ? raw : n;
    }
    setDraft((d) => deepSet(d, path, value));
  };

  const setArray = (path, text) => {
    try {
      const parsed = JSON.parse(text);
      setDraft((d) => deepSet(d, path, parsed));
    } catch (e) {
      // keep editing until valid JSON
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.saveConfig(draft, token);
      toast.success("Config saved. The website now reflects these values.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="admin-config-editor">
      <div className="flex items-center justify-between gap-4 rounded-2xl border border-carza/25 bg-carza/[0.05] px-5 py-4">
        <p className="text-[13px] text-mist">
          Edit traction numbers, unit economics, service prices, contacts and revenue projections. Leave a value empty to show it as "to be finalized".
        </p>
        <button onClick={save} disabled={saving || !dirty} className="btn btn-primary shrink-0 px-5 py-2.5 text-[13px] disabled:opacity-40" data-testid="admin-config-save">
          <Save size={14} /> {saving ? "Saving…" : "Save Config"}
        </button>
      </div>
      <div className="grid grid-cols-1 gap-4 rounded-2xl border border-white/5 p-5 sm:grid-cols-2 lg:grid-cols-3">
        {fields.filter((f) => !Array.isArray(f.value) && f.value === null || typeof f.value !== "object").map((f) => (
          <label key={f.path} className="flex flex-col gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mist-2">{f.path}</span>
            <input
              className="field py-2.5"
              value={f.value == null ? "" : f.value}
              onChange={(e) => setField(f.path, e.target.value)}
              data-testid={`config-input-${f.path.replace(/\./g, "-")}`}
            />
          </label>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {fields.filter((f) => Array.isArray(f.value)).map((f) => (
          <label key={f.path} className="flex flex-col gap-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-mist-2">{f.path} (JSON — one item per object)</span>
            <textarea
              className="field min-h-[110px] resize-y font-mono text-xs"
              value={JSON.stringify(f.value, null, 1)}
              onChange={(e) => setArray(f.path, e.target.value)}
              data-testid={`config-array-${f.path.replace(/\./g, "-")}`}
            />
          </label>
        ))}
      </div>
    </div>
  );
}
